import { useState } from "react";

import DeleteIcon from "@mui/icons-material/DeleteOutline";
import GroupIcon from "@mui/icons-material/Group";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listUsers } from "@/api/users";
import {
  createMyBackup,
  deleteMyBackup,
  listMyBackups,
  type UserBackup,
} from "@/api/userBackups";
import { useAuthStore } from "@/store/authStore";

function toLocalInput(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(local: string): string {
  return new Date(local).toISOString();
}

export function CoordinatorBackupPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.me);

  const backups = useQuery({
    queryKey: ["my-backups"],
    queryFn: listMyBackups,
  });

  // Inline candidates lookup — only triggered when user types in the picker
  const [q, setQ] = useState("");
  const candidates = useQuery({
    queryKey: ["backup-candidates", q],
    queryFn: () => listUsers({ q: q || undefined, status: "ACTIVE", limit: 25 }),
    enabled: q.length >= 2,
  });

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [startsAt, setStartsAt] = useState(
    toLocalInput(new Date(Date.now() + 60 * 60_000).toISOString()),
  );
  const [endsAt, setEndsAt] = useState(
    toLocalInput(new Date(Date.now() + 24 * 60 * 60_000).toISOString()),
  );
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      createMyBackup({
        backup_user_id: selectedUserId!,
        starts_at: fromLocalInput(startsAt),
        ends_at: fromLocalInput(endsAt),
        reason: reason || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-backups"] });
      setSelectedUserId(null);
      setSelectedLabel("");
      setReason("");
      setFormError(null);
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setFormError(detail ?? (e as Error).message);
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteMyBackup(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-backups"] }),
  });

  const myId = me?.id;
  const rows = backups.data ?? [];
  const primaryRows = rows.filter((r) => r.primary_user_id === myId);
  const coveringRows = rows.filter((r) => r.backup_user_id === myId);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <GroupIcon />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t("backup.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("backup.subtitle")}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t("backup.createTitle")}
          </Typography>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Autocomplete
              size="small"
              sx={{ flex: 1, minWidth: 220 }}
              options={candidates.data?.items ?? []}
              getOptionLabel={(o) => o.full_name ? `${o.full_name} (${o.email})` : o.email}
              filterOptions={(x) => x}
              loading={candidates.isFetching}
              inputValue={selectedLabel}
              onInputChange={(_, v) => {
                setSelectedLabel(v);
                setQ(v);
              }}
              onChange={(_, v) => setSelectedUserId(v?.id ?? null)}
              renderInput={(params) => (
                <TextField {...params} label={t("backup.backupUser")} />
              )}
            />
            <TextField
              size="small"
              type="datetime-local"
              label={t("backup.startsAt")}
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="datetime-local"
              label={t("backup.endsAt")}
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label={t("backup.reason")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              inputProps={{ maxLength: 255 }}
            />
            <Button
              variant="contained"
              onClick={() => create.mutate()}
              disabled={
                !selectedUserId ||
                !startsAt ||
                !endsAt ||
                create.isPending
              }
            >
              {t("backup.create")}
            </Button>
          </Stack>
          {formError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {t(`backup.err.${formError}`, { defaultValue: formError })}
            </Alert>
          )}
        </Box>

        {backups.isLoading && <CircularProgress size={20} />}

        {rows.length === 0 && !backups.isLoading && (
          <Alert severity="info">{t("backup.empty")}</Alert>
        )}

        {primaryRows.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {t("backup.iAmCovered")}
            </Typography>
            <BackupTable rows={primaryRows} showDelete onDelete={(id) => del.mutate(id)} userLabel={(r) => r.backup_email ?? r.backup_user_id} />
          </Box>
        )}

        {coveringRows.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {t("backup.iAmCovering")}
            </Typography>
            <BackupTable rows={coveringRows} showDelete={false} userLabel={(r) => r.primary_email ?? r.primary_user_id} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}


function BackupTable({
  rows,
  userLabel,
  showDelete,
  onDelete,
}: {
  rows: UserBackup[];
  userLabel: (r: UserBackup) => string;
  showDelete: boolean;
  onDelete?: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("backup.user")}</TableCell>
            <TableCell>{t("backup.startsAt")}</TableCell>
            <TableCell>{t("backup.endsAt")}</TableCell>
            <TableCell>{t("backup.reason")}</TableCell>
            <TableCell>{t("backup.status")}</TableCell>
            {showDelete && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell sx={{ fontFamily: "monospace" }}>{userLabel(r)}</TableCell>
              <TableCell>{new Date(r.starts_at).toLocaleString()}</TableCell>
              <TableCell>{new Date(r.ends_at).toLocaleString()}</TableCell>
              <TableCell>{r.reason ?? "—"}</TableCell>
              <TableCell>
                {r.active ? (
                  <Chip size="small" color="success" label={t("backup.active")} />
                ) : (
                  <Chip size="small" variant="outlined" label={t("backup.scheduled")} />
                )}
              </TableCell>
              {showDelete && (
                <TableCell align="right">
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => onDelete?.(r.id)}
                  >
                    {t("common.delete")}
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
